import React, {useState} from 'react';
import {Box, Center, KeyboardAvoidingView} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';
import {StackParamList} from '../../navigators';
import ExternalTemplate from '../../components/templates/external';
import {PrimaryButton} from '../../components/buttons';
import {TextInput as Input} from './../../components/inputs/text';
import {IRecoverForm} from '../../interfaces/ui';
import {checkIsValidEmail} from '../../utils/validations';
import useToast from '../../hooks';
import {useContextState} from '../../context/user';
import {SpinnerModal} from '../../components/modals';
import {ResultState} from '../../constants/enumerations';
import {message} from '../../utils/messages';

type ScreenProps = NativeStackScreenProps<StackParamList, 'PasswordRecover'>;

export const PasswordRecoverScreen = ({navigation}: ScreenProps) => {
  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
    } as IRecoverForm,
  });

  const [loading, setLoading] = useState(false);
  const {showServerError, showInputsError, showRecoverError} = useToast();
  const {recoverPassword} = useContextState();

  const onSubmit: SubmitHandler<IRecoverForm> = async (data: IRecoverForm) => {
    setLoading(true);
    const result = await recoverPassword(data.email.toLocaleLowerCase());

    if (result === ResultState.success) {
      navigation.navigate('ResetSuccess');
    } else if (result === ResultState.rejected) {
      showRecoverError();
    } else {
      showServerError();
    }
    setLoading(false);
  };

  const onInvalid: SubmitErrorHandler<IRecoverForm> = async (
    fieldErrors: FieldErrors<IRecoverForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleGoBack = () => {
    navigation.navigate('SignIn');
  };

  return (
    <ExternalTemplate
      title="password-recover-title"
      summary="password-recover-summary"
      backButton={{
        onPress: () => handleGoBack(),
      }}>
      <KeyboardAvoidingView behavior="height" flex="1" enabled={true}>
        <SpinnerModal show={loading} title={message('spinner-sending')} />
        <Center width="100%" flex={1} justifyContent="flex-start">
          <Box width="100%">
            <Input
              control={control}
              name="email"
              type="email"
              label="signin-label-email"
              error={errors.email}
              rules={{
                required: 'form-is-required',
                validate: email => checkIsValidEmail(email) || 'form-is-email',
              }}
            />
          </Box>
          <Box width="100%" position="absolute" bottom="0" safeArea mb="5">
            <PrimaryButton
              label="password-recover-send"
              onPress={handleSubmit(onSubmit, onInvalid)}
            />
          </Box>
        </Center>
      </KeyboardAvoidingView>
    </ExternalTemplate>
  );
};
